import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBolt, IconLayers, IconPlus, IconWaves } from '../../components/icons';
import { CodeTabs, Empty, Field, Modal, Skeleton, useAsync, useToast } from '../../components/ui';
import { JOURNEY_STATUS, JourneyListItem, pp, triggerText, Trigger } from '../../journeys/model';
import { api } from '../../lib/api';
import { ago, num } from '../../lib/format';
import { t } from '../../lib/i18n';
import { canWrite, useSession } from '../../lib/session';
import { errorText } from '../Contacts';
import { JourneyLibrary } from './JourneyLibrary';
import '../../styles/journeys.css';

export function asCodeSnippets(key = 'abandoned-cart') {
  const host = window.location.origin;
  return {
    cURL: `curl -X PUT ${host}/v1/journeys/${key} \\
  -H "Authorization: Bearer $MAILOCEAN_KEY" \\
  -H "Content-Type: application/json" \\
  -d @journeys/${key}.json      # { "graph": { "schema": "mailocean.journey/v1", … } }
# ${t('Ответ: { "version": 3, "hash": "…", "created": true, "approval": { "status": "none" } }')}
# ${t('Повтор того же графа — created: false (безопасно для CI).')}`,
    'Node.js': `import { readFile } from 'node:fs/promises';
import { MailOcean } from '@mailocean/sdk';
const mo = new MailOcean(process.env.MAILOCEAN_KEY);

const graph = JSON.parse(await readFile('journeys/${key}.json', 'utf8'));
const check = await mo.journeys.validate('${key}', graph); // ${t('ничего не пишет')}
const v = await mo.journeys.put('${key}', { graph });
await mo.journeys.requestApproval('${key}', v.version, v.hash);
// ${t('Согласует владелец или администратор в кабинете, потом:')}
await mo.journeys.publish('${key}', v.version, v.hash);`,
    Python: `import json, os
from mailocean import MailOcean
mo = MailOcean(os.environ["MAILOCEAN_KEY"])

graph = json.load(open("journeys/${key}.json"))
mo.journeys.validate("${key}", graph)          # ${t('ничего не пишет')}
v = mo.journeys.put("${key}", graph)
mo.journeys.request_approval("${key}", v["version"], v["hash"])
# ${t('Согласует владелец или администратор в кабинете, потом:')}
mo.journeys.publish("${key}", v["version"], v["hash"])`,
    Go: `mo, _ := mailocean.New(os.Getenv("MAILOCEAN_KEY"))
v, err := mo.Journeys.Put(ctx, "${key}", graph) // ${t('Created == false, если ничего не изменилось')}
_, err = mo.Journeys.RequestApproval(ctx, "${key}", v.Version, v.Hash)
// ${t('Согласует владелец или администратор в кабинете, потом:')}
_, err = mo.Journeys.Publish(ctx, "${key}", v.Version, v.Hash)`,
  };
}

export function JourneyStatusPill({ j }: { j: { status: keyof typeof JOURNEY_STATUS; locked_by_staff?: boolean } }) {
  const s = JOURNEY_STATUS[j.status];
  return (
    <>
      <span className={`pill ${s.tone}`} title={t(s.hint)}>{t(s.label)}</span>
      {j.locked_by_staff && <span className="pill bad" title={t('Остановлено MailOcean: возобновить можно после обращения в поддержку')}>MailOcean</span>}
    </>
  );
}

function LiftLine({ m }: { m: JourneyListItem['measurement'] }) {
  if (!m) return <div className="current-lift muted">{t('Не опубликован — метрик нет.')}</div>;
  if (!m.goal) return <div className="current-lift muted">{t('Цель не задана — прирост не измеряется.')}</div>;
  if (!m.incremental.available) return <div className="current-lift">{t('Цель:')} <code>{m.goal.event}</code> {t('за {n} дн.', { n: m.goal.window_days })} · <span className="muted">{t('прирост не измеряется (нет контрольной группы)')}</span></div>;
  const inc = m.incremental;
  return (
    <div className="current-lift">
      {t('Цель:')} <code>{m.goal.event}</code> · {t('прирост')}{' '}
      {inc.min_sample_ok && inc.lift_abs != null ? (
        <b title={inc.ci95_abs ? t('95 % интервал: {a} … {b}', { a: pp(inc.ci95_abs[0]), b: pp(inc.ci95_abs[1]) }) : undefined}>{pp(inc.lift_abs)}{inc.significant ? '' : ` (${t('незначимо')})`}</b>
      ) : (
        <span className="muted">{t('данных пока мало')}</span>
      )}
    </div>
  );
}

export function JourneysPage() {
  const { me } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const list = useAsync(() => api.get<JourneyListItem[]>('/journeys'));
  const [library, setLibrary] = useState(false);
  const [code, setCode] = useState(false);
  const [scratch, setScratch] = useState(false);
  const writer = canWrite(me) && !me?.support;

  async function toggle(j: JourneyListItem) {
    try {
      await api.post(`/journeys/${j.id}/${j.status === 'active' ? 'pause' : 'resume'}`);
      toast(j.status === 'active' ? t('Сценарий на паузе: шаги ждут') : t('Сценарий снова течёт'));
      list.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Сценарии')}</h1>
          <p>{t('Течения писем: триггер → ожидания и ветки → письма. Каждая версия запускается только после согласования владельцем или администратором; участник — капля в течении.')}</p>
        </div>
        <div className="row wrap">
          <button className="btn" onClick={() => setCode(true)}><IconBolt size={15} /> {t('Как код')}</button>
          {writer && <button className="btn" onClick={() => setScratch(true)}><IconPlus size={15} /> {t('С нуля')}</button>}
          {writer && <button className="btn btn-primary" onClick={() => setLibrary(true)}><IconLayers size={15} /> {t('Из шаблона')}</button>}
        </div>
      </div>
      {list.loading && !list.data ? (
        <div className="currents"><Skeleton h={180} /><Skeleton h={180} /></div>
      ) : list.error ? (
        <div className="callout bad">{errorText(list.error)}</div>
      ) : !list.data?.length ? (
        <div className="card">
          <Empty icon={<IconWaves size={36} />} title={t('Сценариев пока нет')} action={writer ? <button className="btn btn-primary" onClick={() => setLibrary(true)}>{t('Выбрать шаблон')}</button> : undefined}>
            {t('Начните с шаблона — «Добро пожаловать», «Брошенная корзина» или «Возврат» — или опишите сценарий в JSON и отправьте через API из CI.')}
          </Empty>
        </div>
      ) : (
        <div className="currents">
          {list.data.map((j) => (
            <div key={j.id} className={`card current-card ${j.status}`} role="link" tabIndex={0} onClick={() => nav(`/journeys/${j.id}`)} onKeyDown={(e) => e.key === 'Enter' && nav(`/journeys/${j.id}`)}>
              <div className="flow" aria-hidden="true" />
              <div className="row wrap" style={{ gap: 6 }}>
                <JourneyStatusPill j={j} />
                <span className="small muted">v{j.active_version ?? '—'}{j.latest_version !== j.active_version ? ` · ${t('последняя v{v}', { v: j.latest_version })}` : ''}</span>
              </div>
              <h3>{j.name}</h3>
              <div className="small muted" style={{ overflowWrap: 'anywhere' }}><code>{j.key}</code>{j.trigger ? ` · ${j.trigger.type === 'event' ? t('событие {ref}', { ref: String(j.trigger.ref) }) : j.trigger.type === 'segment_entry' ? t('сегмент {ref}', { ref: String(j.trigger.ref) }) : j.trigger.type === 'date' ? t('дата {ref}', { ref: String(j.trigger.ref) }) : 'API'}` : ''}</div>
              <div className="current-metrics">
                <div><b>{num(j.enrollments.active)}</b><span>{t('капель в пути')}</span></div>
                <div><b>{num(j.enrollments.control)}</b><span>{t('в контроле')}</span></div>
                <div title={t('Письма сценария за 7 дней, которые принял провайдер (не «доставлено»)')}><b>{num(j.emails_7d?.accepted ?? 0)}</b><span>{t('писем за 7 дн., принято')}</span></div>
              </div>
              <LiftLine m={j.measurement} />
              <div className="current-actions" onClick={(e) => e.stopPropagation()}>
                {writer && (j.status === 'active' || (j.status === 'paused' && !j.locked_by_staff)) && (
                  <button className="btn btn-sm" onClick={() => toggle(j)}>{j.status === 'active' ? t('Пауза') : t('Возобновить')}</button>
                )}
                <span className="spacer" />
                <span className="small muted">{t('изменён {ago}', { ago: ago(j.updated_at) })}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {scratch && <NewJourneyModal onClose={() => setScratch(false)} onCreated={(id) => nav(`/journeys/${id}`)} />}
      {library && <JourneyLibrary onClose={() => setLibrary(false)} onCreated={(id) => nav(`/journeys/${id}`)} />}
      {code && (
        <Modal title={t('Сценарии как код')} onClose={() => setCode(false)}>
          <p className="small">{t('Граф — JSON по схеме')} <code>mailocean.journey/v1</code>. <code>PUT /v1/journeys/:key</code> {t('создаёт новую неизменяемую версию, только если граф изменился; согласовать её может только человек в кабинете (через API-ключ — нельзя). Нужны скоупы')} <code>journeys:read</code> {t('и')} <code>journeys:write</code>.</p>
          <CodeTabs snippets={asCodeSnippets()} />
          <p className="small muted">{t('Пример графа:')} <code>{triggerText({ type: 'event', event: 'checkout.started' } as Trigger)}</code> → {t('ждать')} <code>order.paid</code> {t('1 ч → письмо → … — откройте любой сценарий и вкладку JSON.')}</p>
        </Modal>
      )}
    </>
  );
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[а-яё]/g, (c) => ({ а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya' })[c] ?? '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

/**
 * «С нуля» (docs/pilot/SPEC.md §12): a draft journey whose only step is «Выход» — the person then builds it on the
 * canvas by dragging steps into the edge from «Старт». Nothing runs before an approval.
 */
function NewJourneyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [event, setEvent] = useState('signup');
  const [busy, setBusy] = useState(false);
  const k = key || slug(name);
  const keyOk = /^[a-z0-9][a-z0-9-_]{1,63}$/.test(k);
  const eventOk = /^[a-z][a-z0-9_.]{0,63}$/.test(event);
  async function create() {
    setBusy(true);
    try {
      const graph = {
        schema: 'mailocean.journey/v1',
        name: name.trim(),
        trigger: { type: 'event', event },
        delivery: { stream: 'marketing' },
        start: 'exit_1',
        steps: { exit_1: { type: 'exit', reason: 'done' } },
      };
      const r = await api.post<{ id: string; created: boolean }>('/journeys', { key: k, name: name.trim(), graph });
      toast(r.created ? t('Черновик создан: перетащите шаги из палитры на холст') : t('Сценарий с таким ключом уже есть — открываю его'));
      onCreated(r.id);
    } catch (err) {
      toast(errorText(err), 'bad');
      setBusy(false);
    }
  }
  return (
    <Modal title={t('Новый сценарий с нуля')} onClose={onClose} footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-primary" disabled={busy || !name.trim() || !keyOk || !eventOk} onClick={create}>{busy ? t('Создаю…') : t('Создать и открыть редактор')}</button></>}>
      <Field label={t('Название')}><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Например: Повторная покупка')} autoFocus /></Field>
      <Field label={t('Ключ')} hint={keyOk ? t('латиница, цифры, - и _; по нему сценарий доступен из API') : t('латиница в нижнем регистре, цифры, - и _, 2–64 символа')}>
        <input className="input input-mono" value={key} onChange={(e) => setKey(e.target.value.trim())} placeholder={slug(name) || 'repeat-purchase'} />
      </Field>
      <Field label={t('Событие-триггер')} hint={t('Участие начинается с этого события контакта; тип триггера можно сменить на «Старте»')}>
        <input className="input input-mono" value={event} onChange={(e) => setEvent(e.target.value.trim())} />
      </Field>
      <div className="callout">{t('Создастся черновик (версия 1) из одного шага «Выход». Ничего не отправляется: сначала соберите шаги, сохраните версию и запросите согласование.')}</div>
    </Modal>
  );
}
